import { forwardRef, useState } from 'react';
import { buildImageURL, buildResponsiveProps } from '../image-utils';
import { Picture, Image, LoQualityImageWrapper } from './styled-components';

type ImgElementProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

type Props = Omit<ImgElementProps, 'srcSet'> &
  Required<Pick<ImgElementProps, 'src' | 'alt'>> & {
    /**
     * Expected **rendered** image widths (in pixels).
     * Do not include DPR as it will be automatically calculated.
     */
    assetWidths?: number | Array<number>;
    params?: Partial<Omit<Parameters<typeof buildResponsiveProps>[1], 'size'>>;
  };

export const ResponsiveImage = forwardRef<HTMLImageElement, Props>(
  ({ className, assetWidths, params, src, sizes, ...props }, ref) => {
    const [imageLoading, setImageLoading] = useState(true);
    const sourceProps = buildResponsiveProps(src, assetWidths, params);
    const fallbackSourceProps = buildResponsiveProps(src, assetWidths, {
      format: 'jpeg',
    });

    return (
      <Picture className={className}>
        <source sizes={sizes} {...sourceProps} />
        {/* Fallbacks to JPEG for older Safari browsers */}
        <source sizes={sizes} {...fallbackSourceProps} />
        <Image
          ref={ref}
          src={buildImageURL(src, {
            // Fallbacks to JPEG for older Safari browsers
            format: 'jpeg',
          })}
          sizes={sizes}
          srcSet={fallbackSourceProps.srcSet}
          onLoad={() => {
            setImageLoading(false);
          }}
          {...props}
        />
        <LoQualityImageWrapper $visible={imageLoading} {...{ inert: '' }}>
          <Image
            role="none"
            src={buildImageURL(src, { size: '100x0', format: 'jpeg', quality: 80 })}
          />
        </LoQualityImageWrapper>
      </Picture>
    );
  },
);
